// ─── Types ────────────────────────────────────────────────────────────────────

export interface EggProductionRecord {
  id: string;
  recordNumber: string;
  productionDate: string;
  farm: string;
  branch?: string;
  house: string;
  flockId: string;
  flockNumber: string;
  flockName: string;
  breed: string;
  birdType: 'Layer' | 'Breeder' | 'Indigenous';
  ageWeeks: number;
  openingBirdPopulation: number;

  morningCollection: number;
  middayCollection: number;
  eveningCollection: number;
  otherCollection: number;
  totalEggs: number;

  goodEggs: number;
  crackedEggs: number;
  dirtyEggs: number;
  smallEggs: number;
  mediumEggs: number;
  largeEggs: number;
  extraLargeEggs: number;
  rejectedEggs: number;
  hatchingEggs: number;
  doubleYolkEggs: number;
  otherEggs: number;

  traySize: number;
  fullTrays: number;
  looseEggs: number;

  henDayProduction: number;
  goodEggRate: number;
  crackRate: number;
  rejectionRate: number;
  dirtyEggRate: number;
  hatchingEggRate: number;
  eggsPerBird: number;

  targetProduction?: number;
  variance?: number;

  productionNotes?: string;
  observedProblems?: string;
  weatherNotes?: string;
  recordedBy: string;
  status: 'draft' | 'submitted' | 'approved';
  createdAt: string;
  updatedAt: string;
}

export interface EggGradingRecord {
  id: string;
  gradingNumber: string;
  date: string;
  farm: string;
  house: string;
  flockId: string;
  flockNumber: string;
  productionRecordId?: string;
  totalEggsReceived: number;
  gradeA: number;
  gradeB: number;
  smallEggs: number;
  mediumEggs: number;
  largeEggs: number;
  extraLargeEggs: number;
  hatchingEggs: number;
  rejected: number;
  gradedBy: string;
  notes?: string;
  createdAt: string;
}

export interface EggInventoryItem {
  id: string;
  eggType: string;
  grade: string;
  farm: string;
  store: string;
  quantity: number;
  trays: number;
  looseEggs: number;
  productionDate: string;
  batchNumber: string;
  expiryDate: string;
  status: 'available' | 'reserved' | 'expired' | 'damaged';
  openingStock: number;
  produced: number;
  transferredIn: number;
  transferredOut: number;
  sold: number;
  damaged: number;
  rejected: number;
  consumed: number;
  adjusted: number;
  closingStock: number;
}

export interface EggStockMovement {
  id: string;
  movementNumber: string;
  date: string;
  movementType: 'production' | 'transfer_in' | 'transfer_out' | 'sale' | 'damage' | 'consumption' | 'adjustment' | 'return';
  source: string;
  destination: string;
  eggType: string;
  quantity: number;
  trays: number;
  looseEggs: number;
  reference?: string;
  createdBy: string;
  notes?: string;
}

export interface EggTransfer {
  id: string;
  transferNumber: string;
  date: string;
  sourceFarm: string;
  sourceStore: string;
  destinationFarm: string;
  destinationStore: string;
  eggGrade: string;
  quantity: number;
  trays: number;
  looseEggs: number;
  transferredBy: string;
  receivedBy?: string;
  status: 'draft' | 'pending' | 'in_transit' | 'received' | 'cancelled';
  notes?: string;
  createdAt: string;
}

export interface EggDamageRecord {
  id: string;
  damageNumber: string;
  date: string;
  farm: string;
  store: string;
  flockId?: string;
  eggType: string;
  quantity: number;
  reason: 'cracked' | 'broken' | 'spoiled' | 'contaminated' | 'transport_damage' | 'handling_damage' | 'expired' | 'other';
  notes?: string;
  reportedBy: string;
  approvedBy?: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
}

export interface EggProductionSettings {
  defaultTraySize: number;
  productionTargetThreshold: number;
  crackRateThreshold: number;
  rejectRateThreshold: number;
  dirtyEggThreshold: number;
  productionAlertThreshold: number;
  autoStockCreation: boolean;
  approvalRequired: boolean;
  inventoryIntegration: boolean;
  salesIntegration: boolean;
}

export interface FlockProductionSummary {
  flockId: string;
  flockNumber: string;
  flockName: string;
  breed: string;
  ageWeeks: number;
  currentBirds: number;
  house: string;
  eggsToday: number;
  eggsThisWeek: number;
  henDayPct: number;
  targetPct: number;
  variance: number;
  status: 'excellent' | 'good' | 'below_target' | 'critical';
}

export interface EggProductionTrendPoint {
  date: string;
  totalEggs: number;
  goodEggs: number;
  rejectedEggs: number;
  crackedEggs: number;
  dirtyEggs: number;
  hatchingEggs: number;
  henDayPct: number;
}

export interface EggProductionInsight {
  id: string;
  type: 'warning' | 'info' | 'success' | 'critical';
  title: string;
  message: string;
  flock?: string;
  house?: string;
  metric?: string;
  value?: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const EGG_FARMS = ['Idudi Poultry Farm', 'Kampala Demo Farm'];

export const EGG_HOUSES_BY_FARM: Record<string, string[]> = {
  'Idudi Poultry Farm': ['Layer House 01', 'Layer House 02', 'Breeder House 01', 'Free Range Area'],
  'Kampala Demo Farm': ['Layer House 03'],
};

export const EGG_FLOCKS = [
  { id: 'flock-004', flockNumber: 'LY-2026-001', flockName: 'Layers Batch L1 — ISA Brown', breed: 'ISA Brown', farm: 'Idudi Poultry Farm', house: 'Layer House 01', currentBirds: 3150, ageWeeks: 33, birdType: 'Layer' as const },
  { id: 'flock-005', flockNumber: 'LY-2026-002', flockName: 'Layers Batch L2 — Lohmann Brown', breed: 'Lohmann Brown', farm: 'Idudi Poultry Farm', house: 'Layer House 02', currentBirds: 2800, ageWeeks: 28, birdType: 'Layer' as const },
  { id: 'flock-006', flockNumber: 'LY-2026-003', flockName: 'Layers Batch L3 — Lohmann', breed: 'Lohmann Brown', farm: 'Kampala Demo Farm', house: 'Layer House 03', currentBirds: 3940, ageWeeks: 14, birdType: 'Layer' as const },
  { id: 'flock-007', flockNumber: 'BRD-2026-001', flockName: 'Breeders Flock BD1 — Ross', breed: 'Ross 308', farm: 'Idudi Poultry Farm', house: 'Breeder House 01', currentBirds: 2358, ageWeeks: 25, birdType: 'Breeder' as const },
  { id: 'flock-009', flockNumber: 'LOCAL-2026-001', flockName: 'Local Kienyeji Batch 01', breed: 'Kienyeji', farm: 'Idudi Poultry Farm', house: 'Free Range Area', currentBirds: 762, ageWeeks: 22, birdType: 'Indigenous' as const },
];

export const DAMAGE_REASONS = [
  { value: 'cracked', label: 'Cracked' },
  { value: 'broken', label: 'Broken' },
  { value: 'spoiled', label: 'Spoiled' },
  { value: 'contaminated', label: 'Contaminated' },
  { value: 'transport_damage', label: 'Transport Damage' },
  { value: 'handling_damage', label: 'Handling Damage' },
  { value: 'expired', label: 'Expired' },
  { value: 'other', label: 'Other' },
];

export const EGG_GRADES = ['Grade A', 'Grade B', 'Small', 'Medium', 'Large', 'Extra Large', 'Hatching', 'Rejected'];

export const EGG_STORES = ['Main Egg Store', 'Cold Store A', 'Dispatch Area', 'Grading Room'];

// ─── Calculations ─────────────────────────────────────────────────────────────

export function calcHenDayProduction(totalEggs: number, liveBirds: number): number {
  if (liveBirds === 0) return 0;
  return Math.round((totalEggs / liveBirds) * 10000) / 100;
}

export function calcEggsPerBird(totalEggs: number, population: number): number {
  if (population === 0) return 0;
  return Math.round((totalEggs / population) * 100) / 100;
}

export function calcRate(part: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((part / total) * 10000) / 100;
}

export function calcTrays(eggs: number, traySize: number): { fullTrays: number; looseEggs: number } {
  const fullTrays = Math.floor(eggs / traySize);
  const looseEggs = eggs % traySize;
  return { fullTrays, looseEggs };
}

export function getProductionStatus(actual: number, target: number): 'excellent' | 'good' | 'below_target' | 'critical' {
  if (actual >= target) return 'excellent';
  const diff = target - actual;
  if (diff <= 5) return 'good';
  if (diff <= 10) return 'below_target';
  return 'critical';
}

// ─── Mock Production Records ──────────────────────────────────────────────────

export const MOCK_EGG_PRODUCTION_RECORDS: EggProductionRecord[] = [
  {
    id: 'ep-001',
    recordNumber: 'EP-2026-089',
    productionDate: '2026-09-04',
    farm: 'Idudi Poultry Farm',
    house: 'Layer House 01',
    flockId: 'flock-004',
    flockNumber: 'LY-2026-001',
    flockName: 'Layers Batch L1 — ISA Brown',
    breed: 'ISA Brown',
    birdType: 'Layer',
    ageWeeks: 33,
    openingBirdPopulation: 3150,
    morningCollection: 1510,
    middayCollection: 820,
    eveningCollection: 560,
    otherCollection: 0,
    totalEggs: 2890,
    goodEggs: 2750,
    crackedEggs: 45,
    dirtyEggs: 35,
    smallEggs: 120,
    mediumEggs: 980,
    largeEggs: 1480,
    extraLargeEggs: 170,
    rejectedEggs: 60,
    hatchingEggs: 0,
    doubleYolkEggs: 5,
    otherEggs: 0,
    traySize: 30,
    fullTrays: 91,
    looseEggs: 20,
    henDayProduction: 91.75,
    goodEggRate: 95.16,
    crackRate: 1.56,
    rejectionRate: 2.08,
    dirtyEggRate: 1.21,
    hatchingEggRate: 0,
    eggsPerBird: 0.92,
    targetProduction: 94,
    variance: -2.25,
    productionNotes: 'Production slightly below target. Birds appear healthy.',
    observedProblems: 'Minor increase in cracked eggs — check feeder height.',
    weatherNotes: 'Warm morning, 28°C. Adequate ventilation maintained.',
    recordedBy: 'Sarah Namukasa',
    status: 'approved',
    createdAt: '2026-09-04T07:30:00Z',
    updatedAt: '2026-09-04T09:00:00Z',
  },
  {
    id: 'ep-002',
    recordNumber: 'EP-2026-088',
    productionDate: '2026-09-04',
    farm: 'Idudi Poultry Farm',
    house: 'Layer House 02',
    flockId: 'flock-005',
    flockNumber: 'LY-2026-002',
    flockName: 'Layers Batch L2 — Lohmann Brown',
    breed: 'Lohmann Brown',
    birdType: 'Layer',
    ageWeeks: 28,
    openingBirdPopulation: 2800,
    morningCollection: 1180,
    middayCollection: 620,
    eveningCollection: 420,
    otherCollection: 0,
    totalEggs: 2220,
    goodEggs: 2100,
    crackedEggs: 38,
    dirtyEggs: 28,
    smallEggs: 95,
    mediumEggs: 820,
    largeEggs: 1050,
    extraLargeEggs: 135,
    rejectedEggs: 82,
    hatchingEggs: 0,
    doubleYolkEggs: 3,
    otherEggs: 0,
    traySize: 30,
    fullTrays: 74,
    looseEggs: 0,
    henDayProduction: 79.29,
    goodEggRate: 94.59,
    crackRate: 1.71,
    rejectionRate: 3.69,
    dirtyEggRate: 1.26,
    hatchingEggRate: 0,
    eggsPerBird: 0.79,
    targetProduction: 88,
    variance: -8.71,
    productionNotes: 'Production below target. Flock at peak production age.',
    observedProblems: 'Higher rejection rate — investigate feed quality.',
    recordedBy: 'James Okello',
    status: 'submitted',
    createdAt: '2026-09-04T08:00:00Z',
    updatedAt: '2026-09-04T08:00:00Z',
  },
  {
    id: 'ep-003',
    recordNumber: 'EP-2026-087',
    productionDate: '2026-09-03',
    farm: 'Idudi Poultry Farm',
    house: 'Layer House 01',
    flockId: 'flock-004',
    flockNumber: 'LY-2026-001',
    flockName: 'Layers Batch L1 — ISA Brown',
    breed: 'ISA Brown',
    birdType: 'Layer',
    ageWeeks: 33,
    openingBirdPopulation: 3152,
    morningCollection: 1520,
    middayCollection: 830,
    eveningCollection: 570,
    otherCollection: 0,
    totalEggs: 2920,
    goodEggs: 2780,
    crackedEggs: 42,
    dirtyEggs: 30,
    smallEggs: 115,
    mediumEggs: 990,
    largeEggs: 1510,
    extraLargeEggs: 165,
    rejectedEggs: 68,
    hatchingEggs: 0,
    doubleYolkEggs: 4,
    otherEggs: 0,
    traySize: 30,
    fullTrays: 97,
    looseEggs: 10,
    henDayProduction: 92.70,
    goodEggRate: 95.21,
    crackRate: 1.44,
    rejectionRate: 2.33,
    dirtyEggRate: 1.03,
    hatchingEggRate: 0,
    eggsPerBird: 0.93,
    targetProduction: 94,
    variance: -1.30,
    recordedBy: 'Sarah Namukasa',
    status: 'approved',
    createdAt: '2026-09-03T07:45:00Z',
    updatedAt: '2026-09-03T09:15:00Z',
  },
  {
    id: 'ep-004',
    recordNumber: 'EP-2026-086',
    productionDate: '2026-09-03',
    farm: 'Idudi Poultry Farm',
    house: 'Layer House 02',
    flockId: 'flock-005',
    flockNumber: 'LY-2026-002',
    flockName: 'Layers Batch L2 — Lohmann Brown',
    breed: 'Lohmann Brown',
    birdType: 'Layer',
    ageWeeks: 28,
    openingBirdPopulation: 2803,
    morningCollection: 1200,
    middayCollection: 640,
    eveningCollection: 430,
    otherCollection: 0,
    totalEggs: 2270,
    goodEggs: 2150,
    crackedEggs: 40,
    dirtyEggs: 30,
    smallEggs: 100,
    mediumEggs: 840,
    largeEggs: 1080,
    extraLargeEggs: 130,
    rejectedEggs: 80,
    hatchingEggs: 0,
    doubleYolkEggs: 2,
    otherEggs: 0,
    traySize: 30,
    fullTrays: 75,
    looseEggs: 20,
    henDayProduction: 80.99,
    goodEggRate: 94.71,
    crackRate: 1.76,
    rejectionRate: 3.52,
    dirtyEggRate: 1.32,
    hatchingEggRate: 0,
    eggsPerBird: 0.81,
    targetProduction: 88,
    variance: -7.01,
    recordedBy: 'James Okello',
    status: 'approved',
    createdAt: '2026-09-03T08:10:00Z',
    updatedAt: '2026-09-03T10:00:00Z',
  },
  {
    id: 'ep-005',
    recordNumber: 'EP-2026-085',
    productionDate: '2026-09-02',
    farm: 'Idudi Poultry Farm',
    house: 'Layer House 01',
    flockId: 'flock-004',
    flockNumber: 'LY-2026-001',
    flockName: 'Layers Batch L1 — ISA Brown',
    breed: 'ISA Brown',
    birdType: 'Layer',
    ageWeeks: 33,
    openingBirdPopulation: 3154,
    morningCollection: 1490,
    middayCollection: 800,
    eveningCollection: 550,
    otherCollection: 0,
    totalEggs: 2840,
    goodEggs: 2700,
    crackedEggs: 48,
    dirtyEggs: 38,
    smallEggs: 110,
    mediumEggs: 960,
    largeEggs: 1480,
    extraLargeEggs: 150,
    rejectedEggs: 54,
    hatchingEggs: 0,
    doubleYolkEggs: 6,
    otherEggs: 0,
    traySize: 30,
    fullTrays: 94,
    looseEggs: 20,
    henDayProduction: 90.11,
    goodEggRate: 95.07,
    crackRate: 1.69,
    rejectionRate: 1.90,
    dirtyEggRate: 1.34,
    hatchingEggRate: 0,
    eggsPerBird: 0.90,
    targetProduction: 94,
    variance: -3.89,
    recordedBy: 'Sarah Namukasa',
    status: 'approved',
    createdAt: '2026-09-02T07:30:00Z',
    updatedAt: '2026-09-02T09:00:00Z',
  },
  {
    id: 'ep-006',
    recordNumber: 'EP-2026-084',
    productionDate: '2026-09-02',
    farm: 'Idudi Poultry Farm',
    house: 'Breeder House 01',
    flockId: 'flock-007',
    flockNumber: 'BRD-2026-001',
    flockName: 'Breeders Flock BD1 — Ross',
    breed: 'Ross 308',
    birdType: 'Breeder',
    ageWeeks: 25,
    openingBirdPopulation: 2358,
    morningCollection: 1050,
    middayCollection: 480,
    eveningCollection: 320,
    otherCollection: 0,
    totalEggs: 1850,
    goodEggs: 1650,
    crackedEggs: 55,
    dirtyEggs: 40,
    smallEggs: 60,
    mediumEggs: 420,
    largeEggs: 980,
    extraLargeEggs: 190,
    rejectedEggs: 105,
    hatchingEggs: 1650,
    doubleYolkEggs: 0,
    otherEggs: 0,
    traySize: 30,
    fullTrays: 61,
    looseEggs: 20,
    henDayProduction: 78.45,
    goodEggRate: 89.19,
    crackRate: 2.97,
    rejectionRate: 5.68,
    dirtyEggRate: 2.16,
    hatchingEggRate: 89.19,
    eggsPerBird: 0.78,
    targetProduction: 82,
    variance: -3.55,
    recordedBy: 'Ogenga Paul',
    status: 'approved',
    createdAt: '2026-09-02T08:00:00Z',
    updatedAt: '2026-09-02T10:00:00Z',
  },
];

// ─── Trend Data (7 days) ──────────────────────────────────────────────────────

export const MOCK_TREND_DATA: EggProductionTrendPoint[] = [
  { date: 'Aug 29', totalEggs: 5420, goodEggs: 5150, rejectedEggs: 110, crackedEggs: 88, dirtyEggs: 72, hatchingEggs: 1620, henDayPct: 88.2 },
  { date: 'Aug 30', totalEggs: 5380, goodEggs: 5110, rejectedEggs: 118, crackedEggs: 82, dirtyEggs: 70, hatchingEggs: 1610, henDayPct: 87.6 },
  { date: 'Aug 31', totalEggs: 5510, goodEggs: 5240, rejectedEggs: 105, crackedEggs: 90, dirtyEggs: 75, hatchingEggs: 1640, henDayPct: 89.7 },
  { date: 'Sep 01', totalEggs: 5460, goodEggs: 5190, rejectedEggs: 112, crackedEggs: 86, dirtyEggs: 72, hatchingEggs: 1630, henDayPct: 88.9 },
  { date: 'Sep 02', totalEggs: 5540, goodEggs: 5260, rejectedEggs: 108, crackedEggs: 95, dirtyEggs: 77, hatchingEggs: 1650, henDayPct: 90.2 },
  { date: 'Sep 03', totalEggs: 5620, goodEggs: 5340, rejectedEggs: 115, crackedEggs: 88, dirtyEggs: 77, hatchingEggs: 1650, henDayPct: 91.5 },
  { date: 'Sep 04', totalEggs: 5110, goodEggs: 4850, rejectedEggs: 142, crackedEggs: 83, dirtyEggs: 63, hatchingEggs: 1650, henDayPct: 88.6 },
];

// ─── Flock Production Summary ─────────────────────────────────────────────────

export const MOCK_FLOCK_SUMMARIES: FlockProductionSummary[] = [
  {
    flockId: 'flock-004',
    flockNumber: 'LY-2026-001',
    flockName: 'Layers Batch L1 — ISA Brown',
    breed: 'ISA Brown',
    ageWeeks: 33,
    currentBirds: 3150,
    house: 'Layer House 01',
    eggsToday: 2890,
    eggsThisWeek: 20220,
    henDayPct: 91.75,
    targetPct: 94,
    variance: -2.25,
    status: 'good',
  },
  {
    flockId: 'flock-005',
    flockNumber: 'LY-2026-002',
    flockName: 'Layers Batch L2 — Lohmann Brown',
    breed: 'Lohmann Brown',
    ageWeeks: 28,
    currentBirds: 2800,
    house: 'Layer House 02',
    eggsToday: 2220,
    eggsThisWeek: 15400,
    henDayPct: 79.29,
    targetPct: 88,
    variance: -8.71,
    status: 'below_target',
  },
  {
    flockId: 'flock-007',
    flockNumber: 'BRD-2026-001',
    flockName: 'Breeders Flock BD1 — Ross',
    breed: 'Ross 308',
    ageWeeks: 25,
    currentBirds: 2358,
    house: 'Breeder House 01',
    eggsToday: 1850,
    eggsThisWeek: 12950,
    henDayPct: 78.45,
    targetPct: 82,
    variance: -3.55,
    status: 'good',
  },
  {
    flockId: 'flock-006',
    flockNumber: 'LY-2026-003',
    flockName: 'Layers Batch L3 — Lohmann',
    breed: 'Lohmann Brown',
    ageWeeks: 14,
    currentBirds: 3940,
    house: 'Layer House 03',
    eggsToday: 1180,
    eggsThisWeek: 8260,
    henDayPct: 29.95,
    targetPct: 25,
    variance: 4.95,
    status: 'excellent',
  },
  {
    flockId: 'flock-009',
    flockNumber: 'LOCAL-2026-001',
    flockName: 'Local Kienyeji Batch 01',
    breed: 'Kienyeji',
    ageWeeks: 22,
    currentBirds: 762,
    house: 'Free Range Area',
    eggsToday: 320,
    eggsThisWeek: 2240,
    henDayPct: 41.99,
    targetPct: 55,
    variance: -13.01,
    status: 'critical',
  },
];

// ─── Insights ─────────────────────────────────────────────────────────────────

export const MOCK_INSIGHTS: EggProductionInsight[] = [
  {
    id: 'ins-001',
    type: 'warning',
    title: 'Production Below Target',
    message: 'Flock LY-2026-002 production is 79.3%, while the configured target is 88%. This is 8.7% below target.',
    flock: 'LY-2026-002',
    house: 'Layer House 02',
    metric: 'henDayPct',
    value: 79.29,
  },
  {
    id: 'ins-002',
    type: 'critical',
    title: 'Critical Production Drop',
    message: 'Local Kienyeji Batch 01 is 13% below its age-based target of 55%. Immediate management review recommended.',
    flock: 'LOCAL-2026-001',
    house: 'Free Range Area',
    metric: 'henDayPct',
    value: 41.99,
  },
  {
    id: 'ins-003',
    type: 'info',
    title: 'Crack Rate Elevated',
    message: 'Layer House 02 has the highest crack rate this week at 1.71%. Check feeder height and nesting box conditions.',
    house: 'Layer House 02',
    metric: 'crackRate',
    value: 1.71,
  },
  {
    id: 'ins-004',
    type: 'success',
    title: 'Good Egg Rate Improved',
    message: 'Good egg percentage for LY-2026-001 improved from 93.8% to 95.2% compared with last week.',
    flock: 'LY-2026-001',
    metric: 'goodEggRate',
    value: 95.2,
  },
  {
    id: 'ins-005',
    type: 'info',
    title: 'Production Average',
    message: 'Current farm-wide production averages 91.75 eggs per 100 birds for Layer House 01.',
    house: 'Layer House 01',
    metric: 'eggsPerBird',
    value: 91.75,
  },
];

// ─── Inventory Mock Data ──────────────────────────────────────────────────────

export const MOCK_EGG_INVENTORY: EggInventoryItem[] = [
  {
    id: 'inv-001',
    eggType: 'Table Eggs',
    grade: 'Grade A Large',
    farm: 'Idudi Poultry Farm',
    store: 'Main Egg Store',
    quantity: 8420,
    trays: 280,
    looseEggs: 20,
    productionDate: '2026-09-01',
    batchNumber: 'BATCH-2026-089',
    expiryDate: '2026-09-22',
    status: 'available',
    openingStock: 6200,
    produced: 8420,
    transferredIn: 0,
    transferredOut: 2400,
    sold: 3800,
    damaged: 120,
    rejected: 0,
    consumed: 0,
    adjusted: 0,
    closingStock: 8300,
  },
  {
    id: 'inv-002',
    eggType: 'Hatching Eggs',
    grade: 'Hatching Grade',
    farm: 'Idudi Poultry Farm',
    store: 'Cold Store A',
    quantity: 4950,
    trays: 165,
    looseEggs: 0,
    productionDate: '2026-09-02',
    batchNumber: 'BATCH-2026-088',
    expiryDate: '2026-09-16',
    status: 'available',
    openingStock: 3200,
    produced: 4950,
    transferredIn: 0,
    transferredOut: 0,
    sold: 1200,
    damaged: 50,
    rejected: 0,
    consumed: 0,
    adjusted: 0,
    closingStock: 6900,
  },
  {
    id: 'inv-003',
    eggType: 'Table Eggs',
    grade: 'Grade B Medium',
    farm: 'Idudi Poultry Farm',
    store: 'Main Egg Store',
    quantity: 2100,
    trays: 70,
    looseEggs: 0,
    productionDate: '2026-09-03',
    batchNumber: 'BATCH-2026-087',
    expiryDate: '2026-09-24',
    status: 'available',
    openingStock: 1800,
    produced: 2100,
    transferredIn: 0,
    transferredOut: 600,
    sold: 1200,
    damaged: 0,
    rejected: 0,
    consumed: 0,
    adjusted: 0,
    closingStock: 2100,
  },
];

// ─── Grading Records ──────────────────────────────────────────────────────────

export const MOCK_GRADING_RECORDS: EggGradingRecord[] = [
  {
    id: 'gr-001',
    gradingNumber: 'GR-2026-045',
    date: '2026-09-04',
    farm: 'Idudi Poultry Farm',
    house: 'Layer House 01',
    flockId: 'flock-004',
    flockNumber: 'LY-2026-001',
    productionRecordId: 'ep-001',
    totalEggsReceived: 2890,
    gradeA: 2420,
    gradeB: 330,
    smallEggs: 120,
    mediumEggs: 980,
    largeEggs: 1480,
    extraLargeEggs: 170,
    hatchingEggs: 0,
    rejected: 60,
    gradedBy: 'Sarah Namukasa',
    notes: 'Normal grading. Slight increase in small eggs.',
    createdAt: '2026-09-04T10:00:00Z',
  },
  {
    id: 'gr-002',
    gradingNumber: 'GR-2026-044',
    date: '2026-09-03',
    farm: 'Idudi Poultry Farm',
    house: 'Layer House 01',
    flockId: 'flock-004',
    flockNumber: 'LY-2026-001',
    productionRecordId: 'ep-003',
    totalEggsReceived: 2920,
    gradeA: 2450,
    gradeB: 402,
    smallEggs: 115,
    mediumEggs: 990,
    largeEggs: 1510,
    extraLargeEggs: 165,
    hatchingEggs: 0,
    rejected: 68,
    gradedBy: 'Sarah Namukasa',
    createdAt: '2026-09-03T10:30:00Z',
  },
];

// ─── Transfers ────────────────────────────────────────────────────────────────

export const MOCK_TRANSFERS: EggTransfer[] = [
  {
    id: 'tr-001',
    transferNumber: 'ET-2026-012',
    date: '2026-09-04',
    sourceFarm: 'Idudi Poultry Farm',
    sourceStore: 'Main Egg Store',
    destinationFarm: 'Kampala Demo Farm',
    destinationStore: 'Main Egg Store',
    eggGrade: 'Grade A Large',
    quantity: 900,
    trays: 30,
    looseEggs: 0,
    transferredBy: 'James Okello',
    receivedBy: 'Ogenga Paul',
    status: 'received',
    notes: 'Weekly transfer to Kampala branch.',
    createdAt: '2026-09-04T06:00:00Z',
  },
  {
    id: 'tr-002',
    transferNumber: 'ET-2026-011',
    date: '2026-09-03',
    sourceFarm: 'Idudi Poultry Farm',
    sourceStore: 'Cold Store A',
    destinationFarm: 'Idudi Poultry Farm',
    destinationStore: 'Dispatch Area',
    eggGrade: 'Hatching Grade',
    quantity: 600,
    trays: 20,
    looseEggs: 0,
    transferredBy: 'Sarah Namukasa',
    status: 'in_transit',
    createdAt: '2026-09-03T14:00:00Z',
  },
];

// ─── Damage Records ───────────────────────────────────────────────────────────

export const MOCK_DAMAGE_RECORDS: EggDamageRecord[] = [
  {
    id: 'dmg-001',
    damageNumber: 'ED-2026-008',
    date: '2026-09-04',
    farm: 'Idudi Poultry Farm',
    store: 'Main Egg Store',
    flockId: 'flock-004',
    eggType: 'Table Eggs',
    quantity: 45,
    reason: 'handling_damage',
    notes: 'Eggs dropped during tray stacking. Worker trained on proper handling.',
    reportedBy: 'Sarah Namukasa',
    approvedBy: 'James Okello',
    status: 'approved',
    createdAt: '2026-09-04T11:00:00Z',
  },
  {
    id: 'dmg-002',
    damageNumber: 'ED-2026-007',
    date: '2026-09-02',
    farm: 'Idudi Poultry Farm',
    store: 'Cold Store A',
    eggType: 'Hatching Eggs',
    quantity: 30,
    reason: 'expired',
    notes: 'Hatching eggs exceeded 14-day storage limit.',
    reportedBy: 'Ogenga Paul',
    status: 'pending',
    createdAt: '2026-09-02T15:00:00Z',
  },
];

// ─── Settings ─────────────────────────────────────────────────────────────────

export const DEFAULT_SETTINGS: EggProductionSettings = {
  defaultTraySize: 30,
  productionTargetThreshold: 5,
  crackRateThreshold: 3,
  rejectRateThreshold: 5,
  dirtyEggThreshold: 3,
  productionAlertThreshold: 10,
  autoStockCreation: true,
  approvalRequired: true,
  inventoryIntegration: true,
  salesIntegration: true,
};

// ─── Stock Movements Ledger ───────────────────────────────────────────────────

export const MOCK_STOCK_MOVEMENTS: EggStockMovement[] = [
  {
    id: 'mov-001',
    movementNumber: 'MOV-2026-001',
    date: '2026-09-04',
    movementType: 'production',
    source: 'Layer House 01',
    destination: 'Main Egg Store',
    eggType: 'Grade A Large',
    quantity: 2890,
    trays: 96,
    looseEggs: 10,
    reference: 'EP-2026-001',
    createdBy: 'Sarah Namukasa',
    notes: 'Daily production batch',
  },
  {
    id: 'mov-002',
    movementNumber: 'MOV-2026-002',
    date: '2026-09-04',
    movementType: 'sale',
    source: 'Main Egg Store',
    destination: 'Entebbe Hotel Group',
    eggType: 'Grade A Large',
    quantity: 1200,
    trays: 40,
    looseEggs: 0,
    reference: 'ORD-2026-0150',
    createdBy: 'System',
    notes: 'Order delivered — stock deducted automatically',
  },
  {
    id: 'mov-003',
    movementNumber: 'MOV-2026-003',
    date: '2026-09-04',
    movementType: 'transfer_out',
    source: 'Main Egg Store',
    destination: 'Kampala Demo Farm',
    eggType: 'Grade A Large',
    quantity: 900,
    trays: 30,
    looseEggs: 0,
    reference: 'ET-2026-012',
    createdBy: 'James Okello',
    notes: 'Weekly transfer to Kampala branch',
  },
  {
    id: 'mov-004',
    movementNumber: 'MOV-2026-004',
    date: '2026-09-04',
    movementType: 'damage',
    source: 'Main Egg Store',
    destination: 'Waste',
    eggType: 'Table Eggs',
    quantity: 45,
    trays: 1,
    looseEggs: 15,
    reference: 'ED-2026-008',
    createdBy: 'Sarah Namukasa',
    notes: 'Handling damage — approved',
  },
];
